const get = require('lodash/get')
const api = require('./api')
const { wrapEntityApi } = require('../utils/wrap-api')
const { resolveReadSource } = require('../tools/resolve-read-source')
const tools = require('./tools')
const ProgramPgAdapter = require('./data-access/program-pg-adapter')

// Kept under their original names for module.exports (any direct
// raw-function consumers rely on `.list`/`.get` here).
const rawMethods = {
  list: api.listPrograms,
  get: api.getProgram,
  listByIds: api.listProgramsByIds,
  save: api.createProgram,
  update: api.updateProgram,
  addService: api.addService,
  tools
}

// Same functions, but under names that won't shadow the dispatching
// `list`/`get` class methods once wrapped and assigned onto `this` below.
const legacyMethods = {
  listLegacy: api.listPrograms,
  getLegacy: api.getProgram,
  listByIds: api.listProgramsByIds,
  save: api.createProgram,
  update: api.updateProgram,
  addService: api.addService,
  tools
}

class ProgramApi {
  // `pgConnection` is only ever passed from inside agave (see api/index.js) —
  // that's what makes `this.pgAdapter` available there. Callers that want the
  // Postgres-backed reads (the agave program routes) use
  // `program.pgAdapter.listByIds`/`getById` directly rather than wrapper
  // methods here; every other caller (browser, lambdas, data-ops) constructs
  // ProgramApi without one and is unaffected.
  constructor (state, logger, pgConnection, agaveAdapter) {
    this.state = state
    this.agaveAdapter = agaveAdapter

    const apiMethods = wrapEntityApi(legacyMethods, state)
    Object.assign(this, apiMethods)

    if (pgConnection) {
      const username = get(state, 'user.name')
      this.pgAdapter = new ProgramPgAdapter({ pgConnection, username, logger })
    }
  }

  // Two-way dispatch: 'agave' for an online-only browser, 'pouch' for
  // everyone else — and for agave's own fs-api, which has no transport, so
  // the handler serving `/program` answers from couch rather than calling
  // back out to itself. No 'pg' branch here: the pg reads belong to the
  // agave controllers, which use `this.pgAdapter` directly.
  readSource () {
    return resolveReadSource({
      domain: 'program',
      state: this.state,
      hasAgaveTransport: !!this.agaveAdapter
    })
  }

  // `unfiltered` is not a client-decidable option. Server-side callers can
  // legitimately need every tenant's programs (the program sync, report periods,
  // bulk location writes) and keep passing it in-process, but it must never
  // arrive from a client: the agave route's zod schema declares only
  // `programIds`, and its controller scopes to the user's own programs, so there
  // is no every-tenant read an HTTP caller can ask for.
  async list (opts = {}) {
    if (this.readSource() === 'agave') {
      return this.agaveAdapter.get('program', opts)
    }
    return this.listLegacy(opts)
  }

  async get (programId, opts = {}) {
    if (this.readSource() === 'agave') {
      return this.agaveAdapter.get(`program/${programId}`, opts)
    }
    return this.getLegacy(programId, opts)
  }
}

module.exports = rawMethods
module.exports.ProgramApi = ProgramApi
module.exports.ProgramPgAdapter = ProgramPgAdapter
